import { useState } from "react";
import { useFormik } from "formik";
import { Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { object, string, number } from "yup";
import MuiTextField from "../../components/common/text-field/MuiTextField";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import errorResolver from "../../utils/error-resolver";
import { useNotificationContext } from "../../context/notificationContext";

import MuiSelect from "../../components/common/select/MuiSelect";

import { useCanAccess } from "../../hooks/canAccess";
import { AccessEnums } from "../../constants/accessEnum";
import { IBlockNumberSchema } from "../../services/Schemas/BlockNumber";
import { BlockNumbersUpdateRequest } from "../../services/Endpoints/block-numbers/BlockNumbersUpdateEndpoint";
import { BlockNumbersCreateRequest } from "../../services/Endpoints/block-numbers/BlockNumbersCreateEndpoint";

interface ICreateBlocknumbersFrom {
  blockNumber?: Partial<IBlockNumberSchema>;
  onSuccess: () => void;
}

export default function CreateBlocknumbersForm({
  blockNumber,
  onSuccess,
}: ICreateBlocknumbersFrom) {
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();

  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const loginSchema = object({
    number: string().required("شماره ضروری است"),
    group: string().required("گروه ضروری است"),
    description: string().required("توضیحات ضروری است"),
  });

  const formik = useFormik({
    initialValues: {
      number: blockNumber?.number ?? "",
      group: blockNumber?.group ?? "",
      description: blockNumber?.description ?? "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting(true);
      try {
        const { number, group, description } = values;

        const updateRequest = new BlockNumbersUpdateRequest(protectedInstance);
        const createRequest = new BlockNumbersCreateRequest(protectedInstance);

        const resp =
          blockNumber && blockNumber.id
            ? await updateRequest.update(blockNumber.id, {
                number,
                group,
                description,
              })
            : await createRequest.send({
                number,
                group,
                description,
              });

        setWaiting(false);

        formik.resetForm();
        setNotification({
          type: "success",
          message: `شماره بلاک شده با موفقیت ${
            blockNumber ? "آپدیت" : "ایجاد"
          } شد.`,
        });
        onSuccess();
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
      }
    },
    enableReinitialize: true,
  });

  return (
    <Stack
      maxWidth="100%"
      width="100%"
      gap="20.31px"
      mx="auto"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      <MuiTextField
        width="100%"
        label={"شماره بلاک شده"}
        placeholder={"شماره بلاک شده را وارد کنید"}
        name={"number"}
        type="text"
        value={formik.values.number}
        handleChange={formik.handleChange}
        error={
          formik.touched.number &&
          Boolean(formik.errors.number) &&
          formik.errors.number
        }
      />
      <MuiSelect
        placeholder={"گروه را انتخاب کنید"}
        name={"group"}
        label={"گروه"}
        value={formik.values.group}
        handleChange={formik.handleChange}
        options={[
          {
            value: "broadcasting",
            label: "پیام صوتی",
          },
        ]}
      />
      <MuiTextField
        multiline
        width="100%"
        label={"توضیحات"}
        placeholder={"توضیحات را وارد کنید"}
        name={"description"}
        type="text"
        value={formik.values.description}
        handleChange={formik.handleChange}
        error={
          formik.touched.description &&
          Boolean(formik.errors.description) &&
          formik.errors.description
        }
      />
      <LoadingButton
        variant="confirm"
        sx={{
          mt: "10px",
        }}
        type="submit"
        loading={waiting}
        disabled={!can(AccessEnums.AdminPartnerCreate)}
      >
        تایید
      </LoadingButton>
    </Stack>
  );
}
